<template>
  <div>
    <div
      class="d-flex no-gutters"
      style="max-width: 500px; margin-left: auto; margin-right: 0"
    >
      <div class="col pr-1">
        <button
          class="btn btn-sm btn-block btn-secondary text-nowrap"
          @click="$emit('cancel')"
        >
          <i class="fas fa-times pr-2" />
          {{ $t('general.cancel') }}
        </button>
      </div>
      <div class="col pl-1">
        <button
          class="btn btn-sm btn-primary text-nowrap btn-block"
          :disabled="!canGenerate"
          data-test="generate-plan-period"
          @click="generate"
        >
          <span v-if="!pending">
            <i class="fas fa-bolt pr-1" />
            {{ $t('billingPlan.generate') }}
          </span>
          <span v-else>
            <Loader
              color="#fff"
              size="15px"
            />
          </span>
        </button>
      </div>
    </div>
    <hr>

    <div class="small text-secondary mb-3">
      {{ $t('billingPlan.generateInfo') }}
    </div>

    <Loader v-if="pendingPeriods" />
    <div
      v-else-if="periods.length === 0"
      class="text-center text-secondary my-4"
    >
      {{ $t('billingPlan.noPeriods') }}
    </div>
    <div
      v-else
      class="periods"
    >
      <div
        v-for="period in periods"
        :key="period.billingDay"
        class="period d-flex align-items-center justify-content-between"
        :class="{ selected: selected && selected.billingDay === period.billingDay }"
        @click="select(period)"
      >
        <div>
          <div class="font-weight-bold">
            {{ period.title }}
          </div>
          <div class="small text-secondary">
            {{ formatDate(period.billingDay) }}
            <span v-if="!period.executed">
              &middot; {{ $t('billingPlan.periodUpcoming') }}
            </span>
          </div>
        </div>
        <div
          v-if="period.billsCount > 0"
          v-tippy
          :content="$t('billingPlan.periodExists')"
          class="text-warning text-nowrap pl-2"
        >
          <i class="fas fa-file-invoice pr-1" />
          {{ period.billsCount }}
        </div>
      </div>
    </div>

    <div
      v-if="selected && selected.billsCount > 0"
      class="warning-box mt-3"
    >
      <div class="d-flex align-items-center">
        <i class="fas fa-triangle-exclamation" />
        <div>
          {{ $tc('billingPlan.alreadyGenerated', selected.billsCount, { count: selected.billsCount }) }}
        </div>
      </div>
      <BFormCheckbox
        v-model="force"
        class="mt-2"
      >
        {{ $t('billingPlan.generateAnyway') }}
      </BFormCheckbox>
    </div>

    <div
      v-if="error"
      class="text-danger font-weight-bold mt-3"
    >
      {{ error }}
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapActions } from 'vuex';

export default {
  props: {
    plan: Object,
  },
  data: () => ({
    periods: [],
    selected: null,
    force: false,
    pending: false,
    pendingPeriods: false,
    error: null,
  }),
  computed: {
    canGenerate() {
      return !!this.selected
        && !this.pending
        && (this.selected.billsCount === 0 || this.force);
    },
  },
  methods: {
    ...mapActions([
      'getBillingPlanPeriods',
      'executeBillingPlanPeriod',
    ]),
    formatDate(date) {
      return moment(date).format('DD.MM.YYYY');
    },
    select(period) {
      this.selected = period;
      this.force = false;
      this.error = null;
    },
    requestPeriods() {
      this.pendingPeriods = true;
      this.getBillingPlanPeriods({
        params: {
          billingPlanId: this.plan.id,
        },
      })
        .then(({ data }) => {
          this.periods = data;
          if (this.selected) {
            // keep the selection pointing at the refreshed period, so its bill count is current
            this.selected = data.find((x) => x.billingDay === this.selected.billingDay) || null;
            if (this.selected?.billsCount > 0 && !this.force) {
              // the warning below explains the refusal
              this.error = null;
            }
          }
        })
        .finally(() => {
          this.pendingPeriods = false;
        });
    },
    generate() {
      this.pending = true;
      this.error = null;

      this.executeBillingPlanPeriod({
        params: {
          billingPlanId: this.plan.id,
        },
        data: {
          billingDay: this.selected.billingDay,
          force: this.force,
        },
      })
        .then(() => {
          this.pending = false;
          this.$emit('generated');
        })
        .catch(({ response }) => {
          this.pending = false;
          this.error = response?.data?.title || this.$t('error.error');
          // bills may have been created in the meantime - refresh the counts
          this.requestPeriods();
        });
    },
  },
  created() {
    this.requestPeriods();
  },
};
</script>

<style lang="scss" scoped>
  .periods {
    max-height: 50vh;
    overflow-y: auto;
  }

  .period {
    padding: 8px 12px;
    border-radius: 8px;
    border: 1px solid transparent;
    cursor: pointer;
    line-height: 1.3;

    &:hover {
      background-color: #f5f5f5;
    }

    &.selected {
      border-color: $blue;
      background-color: rgba($blue, 0.05);
    }
  }

  .warning-box {
    background-color: rgba($orange, 0.1);
    border-radius: 5px;
    padding: 10px 15px;

    i {
      color: $orange;
      font-size: 20px;
      padding-right: 10px;
    }
  }
</style>
